import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { AppShell } from "./components/AppShell";
import { useAuth } from "./hooks/useAuth";
import { recordInviteDiagnostic } from "./lib/invite-diagnostics";
import { buildInvitePath, readPendingInvitation } from "./lib/invitation-flow";
import { AgentPostgresIntegrationPage } from "./pages/AgentPostgresIntegrationPage";
import { AgentsPage } from "./pages/AgentsPage";
import Datascope from "./components/Datascope/datascope";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { InvitePage } from "./pages/InvitePage";
import { KnowledgeGapsPage } from "./pages/KnowledgeGapsPage";
import { KnowledgebasePage } from "./pages/KnowledgebasePage";
import { LoginPage } from "./pages/LoginPage";
import { QuerygenPage } from "./pages/QuerygenPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { SettingsPage } from "./pages/SettingsPage";
import { UserManagementPage } from "./pages/UserManagementPage";
import { LandingPage } from "./pages/LandingPage";
import { APP_HOME_PATH, APP_ROUTES } from "./routes";

function LegacyAppRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function LegacyAgentIntegrationRedirect({ integration }: { integration: "database" | "postgres" }) {
  const location = useLocation();
  const { agentId } = useParams();
  return (
    <Navigate
      to={`/app/agents/${agentId}/integrations/${integration}${location.search}${location.hash}`}
      replace
    />
  );
}

function ProtectedLayout() {
  const { user, loading, refresh } = useAuth();
  const posthog = usePostHog();
  const location = useLocation();
  const [bootstrappingWorkspace, setBootstrappingWorkspace] = useState(false);
  const pendingInvitation = readPendingInvitation();
  const signedInEmail = user?.email.toLowerCase() ?? null;
  const shouldRedirectToPendingInvitation =
    Boolean(pendingInvitation) &&
    location.pathname !== "/invite" &&
    (!!signedInEmail &&
      (!pendingInvitation?.invitedEmail ||
        pendingInvitation.invitedEmail === signedInEmail));

  useEffect(() => {
    if (
      loading ||
      !user ||
      user.activeOrganizationId ||
      user.organizations.length === 0
    ) {
      setBootstrappingWorkspace(false);
      return;
    }

    let cancelled = false;

    const bootstrapWorkspace = async () => {
      setBootstrappingWorkspace(true);
      recordInviteDiagnostic(posthog, "workspace_bootstrap_started", {
        hasUser: true,
        organizationCount: user.organizations.length,
        activeOrganizationId: user.activeOrganizationId,
      });

      try {
        await refresh();
      } finally {
        if (!cancelled) {
          setBootstrappingWorkspace(false);
        }
      }
    };

    void bootstrapWorkspace();

    return () => {
      cancelled = true;
    };
  }, [loading, refresh, user]);

  useEffect(() => {
    if (!shouldRedirectToPendingInvitation || !pendingInvitation || !signedInEmail) {
      return;
    }

    recordInviteDiagnostic(posthog, "pending_invitation_redirect", {
      invitationId: pendingInvitation.invitationId,
      pendingEmail: pendingInvitation.invitedEmail,
      signedInEmail,
      redirectPath: pendingInvitation.redirectPath,
    });
  }, [pendingInvitation, posthog, shouldRedirectToPendingInvitation, signedInEmail]);

  if (loading || bootstrappingWorkspace) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="rounded-md border border-slate-200 bg-white px-5 py-3 text-sm text-slate-600 shadow-sm">
          Loading workspace...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(
          location.pathname + location.search,
        )}`}
        replace
      />
    );
  }

  if (
    shouldRedirectToPendingInvitation &&
    pendingInvitation
  ) {
    return <Navigate to={buildInvitePath(pendingInvitation)} replace />;
  }

  if (
    !user.activeOrganizationId &&
    user.organizations.length === 0 &&
    location.pathname !== APP_ROUTES.settings
  ) {
    return <Navigate to={`${APP_ROUTES.settings}?tab=organization`} replace />;
  }

  return <AppShell />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<LandingPage />} />

      <Route path="/datascope" element={<LegacyAppRedirect to={APP_ROUTES.datascope} />} />
      <Route path="/pinboards" element={<LegacyAppRedirect to={APP_ROUTES.pinboards} />} />
      <Route path="/knowledge-gaps" element={<LegacyAppRedirect to={APP_ROUTES.knowledgeGaps} />} />
      <Route path="/agents" element={<LegacyAppRedirect to={APP_ROUTES.agents} />} />
      <Route path="/knowledgebase" element={<LegacyAppRedirect to={APP_ROUTES.knowledgebase} />} />
      <Route path="/explore" element={<LegacyAppRedirect to={APP_ROUTES.explore} />} />
      <Route path="/users" element={<LegacyAppRedirect to={APP_ROUTES.users} />} />
      <Route path="/settings" element={<LegacyAppRedirect to={APP_ROUTES.settings} />} />
      <Route
        path="/agents/:agentId/integrations/database"
        element={<LegacyAgentIntegrationRedirect integration="database" />}
      />
      <Route
        path="/agents/:agentId/integrations/postgres"
        element={<LegacyAgentIntegrationRedirect integration="postgres" />}
      />

      <Route path={APP_ROUTES.root} element={<ProtectedLayout />}>
        <Route index element={<Navigate to={APP_HOME_PATH} replace />} />
        <Route path="datascope" element={<QuerygenPage />} />
        <Route path="pinboards" element={<QuerygenPage pinboardsOnly />} />
        <Route path="pinboards/:pinboardId" element={<QuerygenPage pinboardsOnly />} />
        <Route path="knowledge-gaps" element={<KnowledgeGapsPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="knowledgebase" element={<KnowledgebasePage />} />
        <Route path="explore" element={<Datascope />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route
          path="agents/:agentId/integrations/database"
          element={<AgentPostgresIntegrationPage />}
        />
        <Route
          path="agents/:agentId/integrations/postgres"
          element={<AgentPostgresIntegrationPage />}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
